import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Euro, Cigarette, Dog, ShieldCheck, CheckCircle, Users, Heart, X } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENDER_LABEL = { male: 'Hombre', female: 'Mujer', prefer_not_to_say: null };
const NUM_FLATMATES_LABEL = { '1': '1 compañero', '2': '2 compañeros', '3_or_more': '3+ compañeros', 'any': null };
const OFFERS_ROOM_LABEL = { '1': '1 hab.', '2': '2 hab.', 'more_than_2': '+2 hab.' };
const ROOMMATE_PROFILE_STYLES = {
  Abierto:     'bg-pink-100 text-pink-700 border border-pink-200',
  Equilibrado: 'bg-orange-100 text-orange-700 border border-orange-200',
  Reservado:   'bg-violet-100 text-violet-700 border border-violet-200',
};

function compatibilityStyle(score) {
  if (score >= 55) return { backgroundColor: '#22c55e', color: '#ffffff' };
  if (score >= 35) return { backgroundColor: '#facc15', color: '#713f12' };
  return { backgroundColor: '#fb923c', color: '#ffffff' };
}

function InitialsAvatar({ name }) {
  const initials = (name || '?').trim().split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[hsl(144,18%,40%)] to-[hsl(144,18%,28%)]">
      <span className="text-white font-heading font-bold" style={{ fontSize: 'clamp(2.5rem, 10vw, 5rem)' }}>{initials}</span>
    </div>
  );
}

function isNewProfile(createdAt) {
  if (!createdAt) return false;
  const diffDays = (new Date() - new Date(createdAt)) / (1000 * 60 * 60 * 24);
  return diffDays <= 3;
}

const authHeaders = () => ({
  withCredentials: true,
  headers: { ...(localStorage.getItem('access_token') && { Authorization: `Bearer ${localStorage.getItem('access_token')}` }) }
});

export default function ProfileCard({ profile, onRemove }) {
  const cardRef = useRef(null);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [removing, setRemoving] = useState(false);

  const hasPhoto = !!profile.profile_photo;
  const isNew = isNewProfile(profile.created_at);

  useEffect(() => {
    if (!cardRef.current || profile.interest_status !== 'received') return;
    const el = cardRef.current;
    let timeout;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        el.classList.remove('animate-interest-pulse');
        void el.offsetWidth;
        el.classList.add('animate-interest-pulse');
        timeout = setTimeout(() => el.classList.remove('animate-interest-pulse'), 4500);
      }
    }, { threshold: 0.3 });
    observer.observe(el);
    return () => { observer.disconnect(); clearTimeout(timeout); };
  }, [profile.interest_status]);

  const getPhotoUrl = (photo) => {
    if (!photo) return null;
    if (photo.startsWith('http')) return photo;
    return `${API}/files/${photo}`;
  };

  const triggerRemove = () => {
    setRemoving(true);
    setTimeout(() => { if (onRemove) onRemove(profile.user_id); }, 350);
  };

  const handleInterest = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (actionLoading) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/interests`, { target_user_id: profile.user_id }, authHeaders());
      triggerRemove();
    } catch (err) { console.error(err); setActionLoading(false); }
  };

  const handleDismiss = async (e) => {
    e.preventDefault(); e.stopPropagation();
    if (actionLoading) return;
    setActionLoading(true);
    try {
      await axios.post(`${API}/dismissals/${profile.user_id}`, {}, authHeaders());
      triggerRemove();
    } catch (err) { console.error(err); setActionLoading(false); }
  };

  const isComplete = (profile.profile_quality_score || 0) >= 7;
  const genderLabel = profile.gender ? GENDER_LABEL[profile.gender] : null;
  const numFlatmatesLabel = profile.num_flatmates ? NUM_FLATMATES_LABEL[profile.num_flatmates] : null;
  const offersRoomLabel = profile.offers_room ? OFFERS_ROOM_LABEL[profile.offers_room] : null;
  const hasReceivedInterest = profile.interest_status === 'received';
  const roommateProfileStyle = profile.roommate_profile ? ROOMMATE_PROFILE_STYLES[profile.roommate_profile] : null;

  return (
    <div
      ref={cardRef}
      className={`profile-card bg-[hsl(40,50%,99%)] rounded-2xl shadow-sm overflow-hidden transition-all duration-300 ${
        hasReceivedInterest ? 'border-2 border-[hsl(144,18%,55%)]' : 'border border-[hsl(35,15%,90%)]'
      } ${removing ? 'opacity-0 scale-95 pointer-events-none' : 'opacity-100 scale-100'}`}
      data-testid={`profile-card-${profile.user_id}`}
    >
      <Link to={`/profiles/${profile.user_id}`} className="block">
        <div className="aspect-[5/6] relative overflow-hidden bg-[hsl(144,18%,35%)]">
          {(!hasPhoto || imgError) && <InitialsAvatar name={profile.name} />}
          {hasPhoto && !imgError && (
            <>
              {!imgLoaded && <div className="absolute inset-0 bg-gradient-to-br from-[hsl(35,20%,88%)] to-[hsl(35,20%,80%)] animate-pulse" />}
              <img
                src={getPhotoUrl(profile.profile_photo)}
                alt={profile.name}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setImgLoaded(true)}
                onError={() => setImgError(true)}
              />
            </>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

          {/* Top right */}
          <div className="absolute top-3 right-3 flex flex-col gap-1 items-end">
            {profile.email_verified && (
              <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-[hsl(144,18%,35%)] text-[10px] font-medium px-2 py-0.5 rounded-full">
                <ShieldCheck className="w-3 h-3" />Verificado
              </span>
            )}
            {isComplete && (
              <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-[hsl(20,10%,40%)] text-[10px] font-medium px-2 py-0.5 rounded-full">
                <CheckCircle className="w-3 h-3" />Completo
              </span>
            )}
          </div>

          {/* Top left */}
          <div className="absolute top-3 left-3 flex flex-col gap-1 items-start">
            {hasReceivedInterest && (
              <span className="inline-flex items-center gap-1 bg-[hsl(144,18%,45%)] text-white text-[10px] font-medium px-2 py-0.5 rounded-full shadow-sm whitespace-nowrap">
                ¡Le interesas!
              </span>
            )}
            {isNew && (
              <span className="inline-flex items-center gap-1 bg-white/85 backdrop-blur-sm text-[hsl(144,18%,30%)] text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap">
                ✨ Nuevo
              </span>
            )}
            {genderLabel && (
              <span className="inline-flex items-center bg-white/90 backdrop-blur-sm text-[hsl(20,10%,35%)] text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
                {genderLabel}
              </span>
            )}
            {profile.lgbtq_friendly && (
              <span className="inline-flex items-center bg-white/90 backdrop-blur-sm text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
                🏳️‍🌈 LGBTQ+
              </span>
            )}
            {profile.erasmus && (
              <span className="inline-flex items-center bg-white/90 backdrop-blur-sm text-[hsl(20,10%,35%)] text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap">
                ✈️ Erasmus
              </span>
            )}
          </div>

          {/* Nombre + ciudad */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <h3 className="font-heading font-bold text-[22px] text-white leading-tight" style={{ textShadow: '0 1px 3px rgba(0,0,0,0.3)' }}>
              {profile.name}{profile.age ? `, ${profile.age}` : ''}
            </h3>
            {(profile.city || profile.neighborhood) && (
              <p className="flex items-center gap-1.5 text-white/95 text-sm mt-1">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">
                  {profile.city}
                  {profile.city && profile.neighborhood && ' · '}
                  {profile.neighborhood && <span className="capitalize">{profile.neighborhood}</span>}
                </span>
              </p>
            )}
          </div>
        </div>

        <div className="p-4 pb-3">
          <div className="flex items-center justify-between mb-2">
            {profile.budget && (
              <div className="flex items-center gap-1.5 text-[hsl(144,18%,35%)] font-medium text-sm">
                <Euro className="w-4 h-4" /><span>{profile.budget}€/mes</span>
              </div>
            )}
            {roommateProfileStyle && (
              <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${roommateProfileStyle}`}>
                {profile.roommate_profile}
              </span>
            )}
          </div>

          {profile.compatibility_score != null && (
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full" style={compatibilityStyle(profile.compatibility_score)}>
                {profile.compatibility_score}% compatible
              </span>
            </div>
          )}

          {profile.bio && (
            <p className="text-[hsl(20,10%,35%)] text-sm leading-relaxed line-clamp-2 mb-2">{profile.bio}</p>
          )}

          {profile.activity_tags && profile.activity_tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {profile.activity_tags.map(tag => (
                <span key={tag} className="bg-amber-100 text-amber-700 border border-amber-200 text-[10px] font-medium px-2 py-0.5 rounded-full">{tag}</span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 text-xs text-[hsl(20,8%,50%)]">
            {profile.occupation && <span className="truncate max-w-[120px]">{profile.occupation}</span>}
            {profile.is_smoker !== undefined && (
              <span className="flex items-center gap-1 flex-shrink-0">
                <Cigarette className="w-3 h-3" />{profile.is_smoker ? 'Fuma' : 'No fuma'}
              </span>
            )}
            {profile.has_pets && <span className="flex items-center gap-1 flex-shrink-0"><Dog className="w-3 h-3" />Mascotas</span>}
            {numFlatmatesLabel && <span className="flex items-center gap-1 flex-shrink-0"><Users className="w-3 h-3" />{numFlatmatesLabel}</span>}
            {offersRoomLabel && (
              <span className="flex items-center gap-1 flex-shrink-0 text-[hsl(144,18%,35%)] font-medium">
                🏠 {offersRoomLabel}
              </span>
            )}
          </div>
        </div>
      </Link>

      {/* Botones acción */}
      <div className="px-4 pb-4 pt-1 flex gap-2">
        <button
          onClick={handleDismiss}
          disabled={actionLoading}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl border border-[hsl(35,15%,82%)] text-[hsl(20,8%,50%)] text-sm font-medium hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition-colors disabled:opacity-50"
        >
          <X className="w-4 h-4" />No me interesa
        </button>
        <button
          onClick={handleInterest}
          disabled={actionLoading}
          className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl bg-[hsl(144,18%,45%)] text-white text-sm font-medium hover:bg-[hsl(144,18%,38%)] transition-colors disabled:opacity-50"
        >
          <Heart className="w-4 h-4" />Me interesa
        </button>
      </div>
    </div>
  );
}
